import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Timesheet } from "domain/entities/timesheet";
import { TimesheetInputFilter } from "../../domain/models/timesheet-input-filter";
import { Logger } from "../../infra/logger/logger";
import timesheetRepository from "../../infra/repositories/timesheet-repository";
import { TimesheetNotifyInputFilter } from "domain/models/timesheet-notify-input-filter";

export const useTimesheetsByFilter = (filter: TimesheetInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["timesheets", filter],
    queryFn: async () => {
      try {
        const result = await timesheetRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
  });
};

export const useTimesheetsById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["timesheet", id],
    queryFn: async () => {
      try {
        if (!id) return;
        const result = await timesheetRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
  });
};

export const useCreateTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      const result = await timesheetRepository.create(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação da timesheet");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useCreateAllTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      const result = await timesheetRepository.createAll(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação da timesheet");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useUpdateTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      const result = await timesheetRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useBlockAllTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      const result = await timesheetRepository.blockAll(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useUnlockAllTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      const result = await timesheetRepository.unlockAll(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useDeleteTimesheetMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Timesheet) => {
      if (!data || !data.id) throw new Error("Timesheet ID inválido");
      const result = await timesheetRepository.delete(data.id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useTimesheetsNotifier = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TimesheetNotifyInputFilter) => {
      const result = await timesheetRepository.notify(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["timesheetNotify", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};
